import {FlusiaIcon} from '../ui/FlusiaIcon';
import {useFluPilot} from '../../lib/onboarding/consultant/useFluPilot';
import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrowLeft, Search, Sparkles, MessageCircle, Mail, Inbox
} from 'lucide-react';
import { useDynamicHeader } from '../../contexts/DynamicHeaderContext';
import { useTheme } from '../../contexts/ThemeContext';
import { NotificationDropdown } from '../ui/NotificationDropdown';
import { ActivitiesDrawer } from '../ui/ActivitiesDrawer';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { GlobalMinimizedFiles } from '../ui/GlobalMinimizedFiles';
import { useAuth } from '../../lib/auth';
import { usePermissions, useBreakpoints } from '../../lib/hooks';
import { DriveFile } from '../../lib/services/googleDriveService';
import { useTeamChatStore } from '../../lib/stores/teamChatStore';
import { useWhatsAppUnreadTotal } from '../../lib/hooks/whatsapp/useWhatsAppUnreadTotal';
import { useMetaUnreadTotal } from '../../lib/hooks/useMetaUnreadTotal';
import { useWhatsAppDrawerStore } from '../../lib/stores/whatsappDrawerStore';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useMicrosoftConnected } from '../../lib/microsoft/useMicrosoftConnected';
import { HeaderFilterRow } from './header/HeaderFilterRow';
import { HeaderUserMenu } from './header/HeaderUserMenu';
import {
  GmailUnreadBadge, OutlookUnreadBadge, TabsDropdown, StatusBadge, StatBadge,
  ViewModeToggle, ActionsMenu, TAB_THRESHOLD
} from './header/HeaderSubComponents';

interface DynamicHeaderProps {
  menuCollapsed?: boolean;
  setShowProfileModal: (open: boolean) => void;
  setShowChangelogModal: (open: boolean) => void;
  setShowGuideModal: (open: boolean) => void;
  onFeedback: (type: 'bug' | 'feature') => void;
  onFileMaximize: (file: DriveFile) => void;
  onSignOut: () => void;
  onOpenSearch: () => void;
}

export function DynamicHeader({
  menuCollapsed = false,
  setShowProfileModal,
  setShowChangelogModal,
  setShowGuideModal,
  onFeedback,
  onFileMaximize,
  onSignOut,
  onOpenSearch
}: DynamicHeaderProps) {
  const fluPilot=useFluPilot();
  const { config, setIsFiltersOpen } = useDynamicHeader();
  const { isLight } = useTheme();
  const { hasPermission } = usePermissions();
  const canAccessWhatsApp = hasPermission('whatsapp', 'view');
  const { totalUnreadCount } = useTeamChatStore();
  // Badge unico dell'icona Messaggi: non letti WhatsApp + non letti chat di team +
  // conversazioni Instagram/Messenger in attesa di risposta.
  // Le chat silenziate non contano (come su WhatsApp).
  const whatsappUnread = useWhatsAppUnreadTotal();
  const metaPending = useMetaUnreadTotal();
  const messagesDrawerOpen = useWhatsAppDrawerStore((s) => s.isOpen);
  const canAccessMetaMessaging = hasPermission('meta_messaging', 'view');
  const { settings } = useSettingsStore();
  const { user: authUser } = useAuth();
  // Bottone Outlook solo con account Microsoft connesso (parità col gate del drawer).
  const { connected: microsoftConnected } = useMicrosoftConnected(authUser?.id);
  const teamChatEnabled = settings?.team_chat_enabled === true && hasPermission('settings_team_chat', 'view');
  const teamChatUnread = totalUnreadCount();
  const { isMobile } = useBreakpoints();


  // Sincronizza isFiltersOpen con il context (usato dal Layout per margini)
  const hasFiltersRow = !!(config?.filters?.length || config?.customFiltersRow);
  useLayoutEffect(() => {
    setIsFiltersOpen(hasFiltersRow && !config?.filtersVisibleOnMobileOnly);
  }, [hasFiltersRow, config?.filtersVisibleOnMobileOnly, setIsFiltersOpen]);

  // CSS variable per altezza kanban board
  useEffect(() => {
    const headerHeight = 48;
    const filtersHeight = (hasFiltersRow && !config?.filtersVisibleOnMobileOnly) ? 40 : 0;
    const mobileBottomBar = isMobile ? 96 : 0;
    const pageContainerPadding = 16;
    const safetyMargin = 16;
    const totalOffset = headerHeight + filtersHeight + mobileBottomBar + pageContainerPadding + safetyMargin;
    document.documentElement.style.setProperty('--kanban-available-height', `calc(100vh - ${totalOffset}px)`);
  }, [hasFiltersRow, config?.filtersVisibleOnMobileOnly, isMobile]);

  return (
    <>
      {/* Main Header Bar */}
      <div
        className={`${fluPilot?'flex':'hidden sm:flex'} h-12 items-center px-3 gap-3 fixed z-[45] transition-all duration-300 glass-menu ${
          isLight ? '' : 'border border-white/10 rounded-lg'
        }`}
        style={{
          top: isLight ? '0' : 'calc(env(safe-area-inset-top) + 0.5rem)',
          left: fluPilot&&isMobile ? '0' : isLight
            ? (menuCollapsed ? '4rem' : '16rem')
            : (menuCollapsed ? 'calc(4rem + 1rem)' : 'calc(16rem + 1rem)'),
          right: isLight ? '0' : '0.5rem',
        }}
      >
        {/* Mobile already has its own page controls; keep the global actions reachable. */}
        <div className={fluPilot?"hidden md:contents":"contents"}>
        {/* Back button */}
        {config?.showBackButton && (
          <button
            onClick={config?.onBack}
            className="flex-shrink-0 text-foreground hover:text-foreground/60 transition-colors"
            title="Torna indietro"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}

        {/* Icon */}
        {config?.icon && (
          <div className="flex-shrink-0 text-foreground [&>svg]:w-4 [&>svg]:h-4">
            {config.icon}
          </div>
        )}

        {/* Title + Subtitle */}
        {config?.title && !config.titleHidden && (
          <div className="min-w-0 flex-shrink">
            <div className="flex items-center gap-2">
              <h1 className="text-sm font-semibold text-foreground truncate">
                {config.title}
              </h1>
              {config.titleExtra}
              {config.subtitle && (
                <span className="text-xs text-muted-foreground truncate hidden lg:inline">
                  · {config.subtitle}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Status Badge */}
        {config?.statusBadge && <StatusBadge config={config.statusBadge} />}

        {/* Custom Left */}
        {config?.customLeft && (
          <div className={config.customLeftGrow ? 'min-w-0 flex-1 self-stretch' : 'flex-shrink-0'}>{config.customLeft}</div>
        )}

        {/* Tabs */}
        {config?.tabs && config.tabs.length > 0 && (
          config.tabs.length > TAB_THRESHOLD ? (
            <TabsDropdown tabs={config.tabs} activeTab={config.activeTab} onTabChange={config.onTabChange} />
          ) : (
            <div
              className="hidden md:flex items-center gap-0.5 bg-black/5 dark:bg-white/5 rounded-md p-0.5 overflow-x-auto flex-nowrap max-w-[60%] lg:max-w-none"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {config.tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => config.onTabChange?.(tab.id)}
                  className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                    config.activeTab === tab.id
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <span className="[&>svg]:w-3 [&>svg]:h-3">{tab.icon}</span>
                  <span className="hidden lg:inline">{tab.label}</span>
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span className="ml-0.5 px-1 py-0.5 text-[10px] rounded-full bg-red-500 text-white">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )
        )}

        {/* Spacer (non serve se customLeft prende gia' lo spazio libero) */}
        {!config?.customLeftGrow && <div className="flex-1 min-w-0" />}

        {/* Custom Center */}
        {config?.customCenter}

        {/* Stats compatte */}
        {config?.stats && config.stats.length > 0 && (
          <div className="hidden lg:flex items-center gap-2">
            {config.stats.slice(0, 3).map(stat => (
              <StatBadge key={stat.id} stat={stat} />
            ))}
          </div>
        )}

        {/* View Mode Toggle */}
        {config?.viewModes && config.viewModes.length > 0 && (
          <ViewModeToggle modes={config.viewModes} current={config.currentViewMode} onChange={config.onViewModeChange} />
        )}

        {/* Custom Right */}
        {config?.customRight}

        {/* Primary Action */}
        {config?.primaryAction && !config.primaryAction.hidden && (
          <button
            onClick={config.primaryAction.onClick}
            data-tour={config.primaryAction.dataTour}
            className={`inline-flex items-center gap-1 px-2.5 h-8 text-xs font-medium rounded-md transition-colors ${
              config.primaryAction.variant === 'secondary'
                ? 'bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900'
                : config.primaryAction.variant === 'danger'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-[#0f6cbd] hover:bg-[#0a5ba8] text-white'
            }`}
          >
            <span className="[&>svg]:w-3.5 [&>svg]:h-3.5">{config.primaryAction.icon}</span>
            <span className="hidden xl:inline">{config.primaryAction.label}</span>
          </button>
        )}

        {/* Actions Menu */}
        {config?.actions && config.actions.length > 0 && (
          <ActionsMenu actions={config.actions.filter(a => !a.hidden)} />
        )}

        {/* Right section */}
        <div className="hidden md:block">
          <GlobalMinimizedFiles onMaximizeFile={onFileMaximize} />
        </div>

        </div>

        <div className={fluPilot?"ml-auto flex shrink-0 items-center gap-1.5 flusia-header-actions":"flex items-center gap-2.5"} aria-label="Strumenti Flusia">
          {/* SVG gradient per icone header */}
          <svg width="0" height="0" className="absolute">
            <defs>
              <linearGradient id="header-icon-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#E91E8C" />
                <stop offset="100%" stopColor="#6366F1" />
              </linearGradient>
            </defs>
          </svg>

          {/* Search */}
          <button
            onClick={onOpenSearch}
            aria-label="Cerca"
            className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-accent -mr-1"
            data-tooltip-id="header-tooltip"
            data-tooltip-content="Cerca (⌘K)"
          >
            {fluPilot?<FlusiaIcon name="search" size={20}/>:<Search className="h-[18px] w-[18px]" style={{ stroke: 'url(#header-icon-gradient)' }} />}
          </button>

          {/* AI Assistant */}
          {!fluPilot&&<button
            onClick={() => {
              const store = (window as any).__aiHelpStore;
              if (!store) {
                import('../../lib/stores/aiHelpStore').then(({ useAiHelpStore }) => {
                  (window as any).__aiHelpStore = useAiHelpStore;
                  const { widgetState, setWidgetState } = useAiHelpStore.getState();
                  setWidgetState(widgetState === 'minimized' ? 'chat' : 'minimized');
                });
              } else {
                const { widgetState, setWidgetState } = store.getState();
                setWidgetState(widgetState === 'minimized' ? 'chat' : 'minimized');
              }
            }}
            className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-accent"
            data-tooltip-id="header-tooltip"
            data-tooltip-content="Assistente AI"
          >
            <Sparkles className="h-[18px] w-[18px]" style={{ stroke: 'url(#header-icon-gradient)' }} />
          </button>}

          {/* Messaggi: un solo drawer per WhatsApp, Instagram, Messenger e chat di team */}
          {(canAccessWhatsApp || teamChatEnabled || canAccessMetaMessaging) && (() => {
            const total = whatsappUnread + teamChatUnread + metaPending;
            return (
              <button
                onClick={() => useWhatsAppDrawerStore.getState().toggle()}
                className={`relative flex items-center justify-center w-7 h-7 rounded-lg hover:bg-accent ${messagesDrawerOpen ? 'bg-accent' : ''}`}
                data-tooltip-id="header-tooltip"
                data-tooltip-content="Messaggi"
                aria-label="Messaggi"
              >
                {fluPilot?<FlusiaIcon name="chat" size={20}/>:<MessageCircle className="h-[18px] w-[18px]" style={{ stroke: 'url(#header-icon-gradient)' }} />}
                {total > 0 && (
                  <div
                    className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-[16px] px-1 text-white text-[9px] font-semibold rounded-full"
                    style={{ backgroundColor: '#2383E2' }}
                  >
                    {total > 99 ? '99+' : total}
                  </div>
                )}
              </button>
            );
          })()}

          {/* Gmail */}
          {!fluPilot && settings?.google_workspace_enabled && settings?.sync_gmail && (
            <div className="relative">
              <button
                onClick={() => {
                  import('../../lib/stores/gmailDrawerStore').then(({ useGmailDrawerStore }) => {
                    useGmailDrawerStore.getState().toggle();
                  });
                }}
                className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-accent"
                data-tooltip-id="header-tooltip"
                data-tooltip-content="Gmail"
              >
                <Mail className="h-4 w-4" style={{ stroke: 'url(#header-icon-gradient)' }} />
              </button>
              <GmailUnreadBadge />
            </div>
          )}

          {/* Outlook — gate = flag org + account Microsoft CONNESSO (come il drawer e
              /outlook): col solo flag l'utente non connesso vedrebbe un drawer vuoto
              con sync/polling che fallisce per sempre in silenzio. */}
          {!fluPilot && settings?.microsoft_sync_mail && microsoftConnected && (
            <div className="relative">
              <button
                onClick={() => {
                  import('../../lib/stores/outlookDrawerStore').then(({ useOutlookDrawerStore }) => {
                    useOutlookDrawerStore.getState().toggle();
                  });
                }}
                className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-accent"
                data-tooltip-id="header-tooltip"
                data-tooltip-content="Outlook"
              >
                <Inbox className="h-4 w-4" style={{ stroke: 'url(#header-icon-gradient)' }} />
              </button>
              <OutlookUnreadBadge />
            </div>
          )}


          {/* Activities */}
          {!fluPilot&&<ActivitiesDrawer />}

          {/* Notifications */}
          <NotificationDropdown triggerIcon={fluPilot?<FlusiaIcon name="bell" size={20}/>:undefined}/>
          {fluPilot&&<button type="button" className="flu-header-trigger flu-global-trigger" aria-label="Flù, assistente AI" title="Parla con Flù" onClick={()=>window.dispatchEvent(new Event('flusia:flu-open'))}><FlusiaIcon name="flu" size={20}/></button>}

          {/* User Menu */}
          <HeaderUserMenu
            setShowProfileModal={setShowProfileModal}
            setShowChangelogModal={setShowChangelogModal}
            setShowGuideModal={setShowGuideModal}
            onFeedback={onFeedback}
            onSignOut={onSignOut}
          />
        </div>
      </div>

      {/* Filter Row — sempre visibile quando ci sono filtri */}
      {config && (
        <HeaderFilterRow config={config} menuCollapsed={menuCollapsed} />
      )}

      {/* Tooltip */}
      <ReactTooltip
        id="header-tooltip"
        place="bottom"
        delayShow={200}
        style={{
          backgroundColor: '#fff',
          backgroundImage: 'linear-gradient(135deg, rgba(233,30,140,0.06) 0%, rgba(249,115,22,0.06) 100%)',
          color: '#37352F',
          border: '1px solid #E91E8C',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: 500,
          padding: '6px 12px',
          boxShadow: '0 4px 12px rgba(233,30,140,0.15)',
          zIndex: 9999,
        }}
      />
    </>
  );
}
